// @ts-ignore - plain JS effect module without type declarations.
import { setNkfDebugOverlayVisible, setNkfLogVisible } from '../stream-effects/nkf-aec/NkfAecEffect.js';
import { setNkfAecBypass, setNkfAecMakeupGain, setNkfTxMeter } from '../stream-effects/nkf-aec/createNkfAecEffect';
// @ts-ignore - plain JS effect module without type declarations.
import { setNkfForceBypassPct } from '../stream-effects/nkf-aec/NkfAecEffect.js';
import { SET_NKF_AEC_ENABLED, SET_NKF_AEC_READY, SET_NKF_AEC_SETTLING, SET_NKF_DEBUG_OVERLAY, SET_NKF_FORCE_BYPASS, SET_NKF_JANK, SET_NKF_LOG_PANEL, SET_NKF_MAKEUP_GAIN, SET_NKF_MEDIA_STATS, SET_NKF_TX_METER } from './actionTypes';
import { getNkfForceBypass, isNkfDebugOverlayOpen, isNkfJankRunning, isNkfLogOpen, isNkfMakeupGainOn, isNkfMediaStatsOpen, isNkfTxMeterOpen } from './functions';
import { startJank, stopJank } from './jank';
import { setNkfMediaStatsVisible } from './mediaStatsPanel';
/**
 * Turns the NKF-AEC echo cancellation ON or OFF. Updates redux (so the toolbar
 * button reflects the state) and applies the change to the live audio effect.
 *
 * @param {boolean} enabled - True to enable echo cancellation, false to bypass.
 * @returns {Function}
 */
export function setNkfAecEnabled(enabled) {
    return (dispatch) => {
        setNkfAecBypass(!enabled);
        dispatch({
            type: SET_NKF_AEC_ENABLED,
            enabled
        });
    };
}
/**
 * Toggles the NKF-AEC debug waveform overlay (the "スピーカー" button — same as
 * the floating top-left waveform button).
 *
 * @returns {Function}
 */
export function toggleNkfDebugOverlay() {
    return (dispatch, getState) => {
        const visible = !isNkfDebugOverlayOpen(getState());
        setNkfDebugOverlayVisible(visible);
        dispatch({
            type: SET_NKF_DEBUG_OVERLAY,
            visible
        });
    };
}
/**
 * Toggles the NKF-AEC debug LOG panel (the "ログ" item in the 詳細な操作 menu —
 * moved from the floating top-left LOG button).
 *
 * @returns {Function}
 */
export function toggleNkfLogPanel() {
    return (dispatch, getState) => {
        const visible = !isNkfLogOpen(getState());
        setNkfLogVisible(visible);
        dispatch({
            type: SET_NKF_LOG_PANEL,
            visible
        });
    };
}
/**
 * Reports whether echo cancellation has actually reached a working state.
 *
 * Observation only: nothing in the audio path reads this. It is here so a real
 * meeting can tell us whether the "not ready yet" window lines up with the echo
 * loops users hit right after joining — before we act on that signal.
 *
 * @param {boolean} ready - True once cancellation is demonstrably working.
 * @param {string} reason - What decided it ('calibration' | 'tracker' | 'erle' | 'timeout').
 * @returns {Object}
 */
export function setNkfAecReady(ready, reason) {
    return {
        type: SET_NKF_AEC_READY,
        ready,
        reason
    };
}
/**
 * Reports that the echo delay is known but the mic is still held silent while
 * the adaptive filter converges.
 *
 * @param {string} reason - What settled the delay ('calibration' | 'tracker' | 'erle').
 * @returns {Object}
 */
export function setNkfAecSettling(reason) {
    return {
        type: SET_NKF_AEC_SETTLING,
        reason
    };
}
/**
 * Starts/stops the deliberate main-thread jank used to reproduce the
 * transmitted-audio dropouts.
 *
 * Debug tool. It freezes the main thread on purpose so we can check whether the
 * dropouts users report have the shape the diagnosis predicts: the cleaned audio
 * crosses the main thread twice on its way out and the emit worklet buffers only
 * ~96 ms, so a longer stall punches a hole in what the far side hears.
 *
 * Watch `途切れ=空…ms/溢…ms` in the alive log while this runs — 溢 growing is the
 * signature of the thread having been frozen.
 *
 * @returns {Function}
 */
export function toggleNkfJank() {
    return (dispatch, getState) => {
        const state = getState();
        const running = !isNkfJankRunning(state);
        const cfg = state['features/base/config'].nkfAec;
        if (running) {
            startJank(cfg?.jankBlockMs ?? 150, cfg?.jankGapMs ?? 350);
        }
        else {
            stopJank();
        }
        dispatch({
            type: SET_NKF_JANK,
            running
        });
    };
}
/**
 * Shows/hides the media stats panel — main-thread pressure next to the
 * transmitted-audio dropout rate.
 *
 * Measurement runs only while the panel is open, so leaving it closed costs
 * nothing (and the panel itself must not become another source of pressure).
 *
 * @returns {Function}
 */
export function toggleNkfMediaStats() {
    return (dispatch, getState) => {
        const open = !isNkfMediaStatsOpen(getState());
        setNkfMediaStatsVisible(open);
        dispatch({
            type: SET_NKF_MEDIA_STATS,
            open
        });
    };
}
/**
 * Steps the forced-bypass share: OFF → 30% → 100% → OFF.
 *
 * A bypassed chunk goes out as raw mic, so 30% makes cleaned and raw alternate
 * every 32 ms — the alternation we suspect is heard as a warble rather than a
 * dropout. 100% sends nothing but raw mic, which is the reference at the other
 * end (no cancellation at all). Overload cannot be summoned reliably, so this
 * produces the same mixing directly.
 *
 * @returns {Function}
 */
export function cycleNkfForceBypass() {
    return (dispatch, getState) => {
        const current = getNkfForceBypass(getState());
        const pct = current === 0 ? 30 : current < 100 ? 100 : 0;
        setNkfForceBypassPct(pct);
        dispatch({
            type: SET_NKF_FORCE_BYPASS,
            pct
        });
    };
}
/**
 * 音量の自動調整(メイクアップゲイン)を入切する。
 *
 * 生きているエフェクトへ即座に効き、あとで作り直されたエフェクトにも
 * 引き継がれる（createNkfAecEffect が保持する）。グラフは組み替えないので
 * 音は途切れない。
 *
 * @returns {Function}
 */
export function toggleNkfMakeupGain() {
    return (dispatch, getState) => {
        const enabled = !isNkfMakeupGainOn(getState());
        setNkfAecMakeupGain(enabled);
        dispatch({
            type: SET_NKF_MAKEUP_GAIN,
            enabled
        });
    };
}
/**
 * 送信メーター(各測定点の音量と主成分の周波数)を開閉する。
 *
 * 以前は ?nkfMeter=1 でしか出せなかったが、Jitsi が入室時に URL を書き換える
 * ためパラメータが消えてしまい、入室後は開けなかった。
 *
 * @returns {Function}
 */
export function toggleNkfTxMeter() {
    return (dispatch, getState) => {
        const open = !isNkfTxMeterOpen(getState());
        setNkfTxMeter(open);
        dispatch({
            type: SET_NKF_TX_METER,
            open
        });
    };
}
